import cn from 'classnames';
import { FC, ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';

import { transcriptorMessages } from '@/entities/transcriptor';

import {
  type LiveDictationStatus,
  type LiveUnavailableReason,
} from '@/features/audioRecording';

import styles from './LiveDictationNotice.module.scss';

type Props = {
  status: LiveDictationStatus;
  reason: LiveUnavailableReason | null;
  providerError: boolean;
  className?: string;
};

const STATUS_MESSAGES: Record<
  Exclude<LiveDictationStatus, 'unavailable'>,
  ReactNode
> = {
  starting: (
    <FormattedMessage
      id="workspace.live.starting"
      defaultMessage="Starting live dictation…"
    />
  ),
  live: (
    <FormattedMessage
      id="workspace.live.on"
      defaultMessage="Live dictation is on. The protocol fills in as you speak."
    />
  ),
  paused: (
    <FormattedMessage
      id="workspace.live.paused"
      defaultMessage="Live dictation is paused."
    />
  ),
  reconnecting: (
    <FormattedMessage
      id="workspace.live.reconnecting"
      defaultMessage="Live connection lost. Reconnecting…"
    />
  ),
  finishing: (
    <FormattedMessage
      id="workspace.live.finishing"
      defaultMessage="Waiting for the final live answer…"
    />
  ),
  finished: (
    <FormattedMessage
      id="workspace.live.finished"
      defaultMessage="Live recording is complete. The document uses the accepted live answers."
    />
  ),
  interrupted: (
    <FormattedMessage
      id="workspace.live.interrupted"
      defaultMessage="Live recording stopped before completion. Check the saved answers."
    />
  ),
};

export const LiveDictationNotice: FC<Props> = ({
  status,
  reason,
  providerError,
  className,
}) => {
  const degraded =
    (status === 'unavailable' && reason !== 'off') || status === 'interrupted';
  const unavailable =
    reason === 'dictationLimit' ? (
      <FormattedMessage {...transcriptorMessages.dictationLimitReached} />
    ) : (
      <FormattedMessage
        id="workspace.live.unavailable"
        defaultMessage="Live recording is unavailable. Start a new recording."
      />
    );

  return (
    <div
      className={cn(
        'p2',
        styles.notice,
        degraded && styles.degraded,
        className,
      )}
      data-component="workspace-live-status"
      role="status"
    >
      <p>{status === 'unavailable' ? unavailable : STATUS_MESSAGES[status]}</p>
      {providerError && !degraded && (
        <p>
          <FormattedMessage
            id="workspace.live.providerError"
            defaultMessage="The transcription service reported an error. Live text may be incomplete."
          />
        </p>
      )}
    </div>
  );
};
